import React, { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";

const iso = (d) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const addDays = (d, n) => {
	const x = new Date(d);
	x.setDate(x.getDate() + n);
	return x;
};

const addMonths = (d, n) => {
	const x = new Date(d);
	x.setMonth(x.getMonth() + n);
	return x;
};

const daysBetween = (a, b) =>
	Math.round((Date.parse(`${b}T00:00:00`) - Date.parse(`${a}T00:00:00`)) / 86400000) + 1;

const minStr = (a, b) => (a < b ? a : b);
const maxStr = (a, b) => (b && b > a ? b : a);

export const RANGE_PRESETS = [
	{ key: "today", label: "Today" },
	{ key: "7d", label: "7 days" },
	{ key: "14d", label: "14 days" },
	{ key: "30d", label: "30 days" },
	{ key: "month", label: "Current month" },
	{ key: "custom", label: "Custom" },
];

export const COMPARE_PRESETS = [
	{ key: "none", label: "No comparison" },
	{ key: "previous_period", label: "Previous period" },
	{ key: "previous_month", label: "Previous month" },
	{ key: "previous_quarter", label: "Previous quarter" },
	{ key: "previous_year", label: "Previous year" },
	{ key: "custom", label: "Custom" },
];

export function rangeFor(key, today = new Date()) {
	const end = iso(today);
	switch (key) {
		case "today":
			return { start: end, end };
		case "7d":
			return { start: iso(addDays(today, -6)), end };
		case "14d":
			return { start: iso(addDays(today, -13)), end };
		case "30d":
			return { start: iso(addDays(today, -29)), end };
		case "month": {
			const first = new Date(today.getFullYear(), today.getMonth(), 1);
			return { start: iso(first), end };
		}
		default:
			return { start: iso(addDays(today, -13)), end };
	}
}

export function compareFor(key, range) {
	const start = new Date(`${range.start}T00:00:00`);
	const end = new Date(`${range.end}T00:00:00`);
	const span = daysBetween(range.start, range.end);
	switch (key) {
		case "none":
			return null;
		case "previous_period":
			return {
				start: iso(addDays(start, -span)),
				end: iso(addDays(start, -1)),
			};
		case "previous_month":
			return {
				start: iso(addMonths(start, -1)),
				end: iso(addMonths(end, -1)),
			};
		case "previous_quarter":
			return {
				start: iso(addMonths(start, -3)),
				end: iso(addMonths(end, -3)),
			};
		case "previous_year":
			return {
				start: iso(addMonths(start, -12)),
				end: iso(addMonths(end, -12)),
			};
		default:
			return null;
	}
}

export function defaultRangeSelection() {
	const range = rangeFor("14d");
	return { range, compare: compareFor("previous_period", range) };
}

function fmtRange(r) {
	if (!r) return "—";
	const f = (s) =>
		new Date(`${s}T00:00:00`).toLocaleDateString(undefined, {
			day: "numeric",
			month: "short",
		});
	return r.start === r.end ? f(r.start) : `${f(r.start)} – ${f(r.end)}`;
}

function detectRangeKey(r) {
	for (const p of RANGE_PRESETS) {
		if (p.key === "custom") continue;
		const candidate = rangeFor(p.key);
		if (candidate.start === r.start && candidate.end === r.end) return p.key;
	}
	return "custom";
}

function detectCompareKey(range, compare) {
	if (!compare) return "none";
	for (const p of COMPARE_PRESETS) {
		if (p.key === "none" || p.key === "custom") continue;
		const candidate = compareFor(p.key, range);
		if (
			candidate &&
			candidate.start === compare.start &&
			candidate.end === compare.end
		) {
			return p.key;
		}
	}
	return "custom";
}

export default function TrafficDateRangePicker({
	value,
	onChange,
	minDate,
	maxDate,
}) {
	const [open, setOpen] = useState(false);
	const [customRange, setCustomRange] = useState(false);
	const [customCompare, setCustomCompare] = useState(false);
	const [draft, setDraft] = useState(value);
	const boxRef = useRef(null);

	useEffect(() => {
		if (!open) return;
		setDraft(value);
		setCustomRange(detectRangeKey(value.range) === "custom");
		setCustomCompare(detectCompareKey(value.range, value.compare) === "custom");
	}, [open, value]);

	const rangeKey = customRange ? "custom" : detectRangeKey(draft.range);
	const compareKey = customCompare
		? "custom"
		: detectCompareKey(draft.range, draft.compare);

	const applyRange = (key, custom) => {
		setCustomRange(key === "custom");
		const range = key === "custom" ? (custom ?? draft.range) : rangeFor(key);
		const compare =
			compareKey === "custom"
				? draft.compare
				: compareFor(compareKey, range);
		setDraft({ range, compare });
	};

	const applyCompare = (key, custom) => {
		setCustomCompare(key === "custom");
		const compare =
			key === "custom" ? (custom ?? draft.compare) : compareFor(key, draft.range);
		setDraft({ range: draft.range, compare });
	};

	const commit = () => {
		const range =
			draft.range.start <= draft.range.end
				? draft.range
				: { start: draft.range.end, end: draft.range.start };
		onChange({ range, compare: draft.compare });
		setOpen(false);
	};

	const draftDays = daysBetween(draft.range.start, draft.range.end);

	return (
		<div className="monitoring__ai-traffic-date-picker" ref={boxRef}>
			<button
				type="button"
				className={`monitoring__ai-traffic-date-trigger${open ? " monitoring__ai-traffic-date-trigger--open" : ""}`}
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				aria-haspopup="dialog"
			>
				<span>{fmtRange(value.range)}</span>
				{value.compare && (
					<span className="monitoring__ai-traffic-date-trigger-compare">
						vs {fmtRange(value.compare)}
					</span>
				)}
				<span className="monitoring__ai-traffic-date-trigger-chevron" aria-hidden>
					▾
				</span>
			</button>
			{open && (
				<div
					className="monitoring__ai-traffic-date-panel"
					role="dialog"
					aria-label="Date range"
				>
					<div className="monitoring__ai-traffic-date-section-label mb-2">
						Date range
					</div>
					<div className="monitoring__ai-traffic-date-presets">
						{RANGE_PRESETS.map((p) => (
							<button
								key={p.key}
								type="button"
								className={`monitoring__ai-traffic-date-preset${
									rangeKey === p.key
										? " monitoring__ai-traffic-date-preset--active"
										: ""
								}`}
								onClick={() => applyRange(p.key)}
							>
								{p.label}
							</button>
						))}
					</div>
					{rangeKey === "custom" && (
						<div className="monitoring__ai-traffic-date-custom-row">
							<input
								type="date"
								value={draft.range.start}
								min={minDate}
								max={maxDate ? minStr(draft.range.end, maxDate) : draft.range.end}
								aria-label="Range start"
								onChange={(e) =>
									applyRange("custom", {
										...draft.range,
										start: e.target.value,
									})
								}
							/>
							<span>to</span>
							<input
								type="date"
								value={draft.range.end}
								min={maxStr(draft.range.start, minDate)}
								max={maxDate}
								aria-label="Range end"
								onChange={(e) =>
									applyRange("custom", {
										...draft.range,
										end: e.target.value,
									})
								}
							/>
						</div>
					)}

					<div className="monitoring__ai-traffic-date-section-label monitoring__ai-traffic-date-section-label--spaced">
						Compare to
					</div>
					<div className="monitoring__ai-traffic-date-compare">
						{COMPARE_PRESETS.map((p) => (
							<button
								key={p.key}
								type="button"
								className={`monitoring__ai-traffic-date-preset${
									compareKey === p.key
										? " monitoring__ai-traffic-date-preset--active"
										: ""
								}`}
								onClick={() => applyCompare(p.key)}
							>
								{p.label}
							</button>
						))}
					</div>
					{compareKey === "custom" && (
						<div className="monitoring__ai-traffic-date-custom-row">
							<input
								type="date"
								value={draft.compare?.start ?? ""}
								min={minDate}
								max={maxDate}
								aria-label="Comparison start"
								onChange={(e) =>
									applyCompare("custom", {
										start: e.target.value,
										end: draft.compare?.end ?? e.target.value,
									})
								}
							/>
							<span>to</span>
							<input
								type="date"
								value={draft.compare?.end ?? ""}
								min={minDate}
								max={maxDate}
								aria-label="Comparison end"
								onChange={(e) =>
									applyCompare("custom", {
										start: draft.compare?.start ?? e.target.value,
										end: e.target.value,
									})
								}
							/>
						</div>
					)}

					{minDate && draft.range.start < minDate && (
						<p className="monitoring__ai-traffic-date-min-warning mb-0">
							Data starts {fmtRange({ start: minDate, end: minDate })} — earlier
							days will be empty.
						</p>
					)}

					<p className="monitoring__ai-traffic-date-summary mb-0">
						{fmtRange(draft.range)} ({draftDays} day{draftDays === 1 ? "" : "s"})
						{draft.compare
							? ` compared with ${fmtRange(draft.compare)}`
							: " — no comparison, so deltas are hidden"}
					</p>

					<div className="monitoring__ai-traffic-date-actions">
						<Button
							size="sm"
							className="monitoring__action-btn"
							onClick={() => setOpen(false)}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							color="primary"
							className="monitoring__action-btn monitoring__action-btn--primary"
							onClick={commit}
						>
							Apply
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
